import { IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';
import { useCallback, useState, FC } from 'react';
import { Icon } from '@mdi/react';
import { mdiDatabase, mdiDownload, mdiFilterVariant } from '@mdi/js';
import { FormattedMessage } from 'react-intl';

export type DownloadFileButtonProps = {
  onExportComplete: () => Promise<void>;
  onExportWithFilters: () => Promise<void>;
  hasActiveFilters?: boolean;
};

const DownloadFileButton: FC<DownloadFileButtonProps> = ({
  onExportComplete,
  onExportWithFilters,
  hasActiveFilters = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingCount, setLoadingCount] = useState(0);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleDownloadCompleteClick = useCallback(async () => {
    setLoadingCount((prev) => prev + 1);
    handleClose();

    try {
      await onExportComplete();
    } finally {
      setLoadingCount((prev) => prev - 1);
    }
  }, [onExportComplete, handleClose]);

  const handleDownloadFilteredClick = useCallback(async () => {
    setLoadingCount((prev) => prev + 1);
    handleClose();

    try {
      await onExportWithFilters();
    } finally {
      setLoadingCount((prev) => prev - 1);
    }
  }, [onExportWithFilters, handleClose]);

  return (
    <>
      <Tooltip title={<FormattedMessage id="tooltip.download_file" defaultMessage="Export to xlsx" />}>
        <IconButton
          size="small"
          color="inherit"
          id="file-download-button"
          aria-controls={open ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={
            loadingCount > 0
              ? {
                  animation: 'download-icon-pulse 1.2s ease-in-out infinite',
                  '@keyframes download-icon-pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }
              : undefined
          }
        >
          <Icon path={mdiDownload} size={1} />
        </IconButton>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-labelledby': 'file-download-button',
        }}
      >
        <ListSubheader
          component="div"
          disableSticky
          disableGutters
          sx={{ fontSize: 11, fontWeight: 600, py: 0.5, px: 1.5, lineHeight: 1.3 }}
        >
          <FormattedMessage id="file_download.header" defaultMessage="Export to xlsx" />
        </ListSubheader>
        <MenuItem onClick={handleDownloadFilteredClick} disabled={!hasActiveFilters}>
          <ListItemIcon>
            <Icon path={mdiFilterVariant} size={0.8} />
          </ListItemIcon>
          <ListItemText
            primary={
              <FormattedMessage id="file_download.option.current_filter" defaultMessage="Export filtered rows" />
            }
          />
        </MenuItem>
        <MenuItem onClick={handleDownloadCompleteClick}>
          <ListItemIcon>
            <Icon path={mdiDatabase} size={0.8} />
          </ListItemIcon>
          <ListItemText
            primary={<FormattedMessage id="file_download.option.all_data" defaultMessage="Export all rows" />}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default DownloadFileButton;
